import { apiService, type AuthenticatedUser } from './api';
import { logger } from '../utils/logger';
import { crptgrf } from '../utils/crptgrf';
// import { crptgrf } from '../utils/crptgrf';

/**
 * Serviço de autenticação para gerenciar login e dados do usuário
 */
export class AuthService {
    private static readonly USER_STORAGE_KEY = 'authenticated_user';
    private static readonly LOGIN_TIME_KEY = 'login_timestamp';

    /**
     * Realiza login do usuário
     */
    async login(documento: string, senha: string): Promise<{ success: boolean; user?: AuthenticatedUser; error?: string }> {
        try {
            logger.log('🔐 Tentando autenticar usuário:', { documento });

            const senhaCriptografada = crptgrf(senha, true)
            
            const response = await apiService.authenticateUser(documento, senhaCriptografada);

            if (response.success && response.data) {
                // Armazenar dados do usuário localmente
                this.storeUserData(response.data);

                logger.log('✅ Login realizado com sucesso:', {
                    user: response.data.notificador,
                    id: response.data.id_location
                });

                return {
                    success: true,
                    user: response.data
                };
            } else {
                logger.error('❌ Falha na autenticação:', response.error);
                return {
                    success: false,
                    error: response.error || 'Credenciais inválidas'
                };
            }
        } catch (error: any) {
            const errorMessage = error.message || 'Erro de conexão com o servidor';
            logger.error('💥 Erro durante login:', error);
            return {
                success: false,
                error: errorMessage
            };
        }
    }

    /**
     * Armazena dados do usuário no localStorage
     */
    private storeUserData(user: AuthenticatedUser): void {
        try {
            localStorage.setItem(AuthService.USER_STORAGE_KEY, JSON.stringify(user));
            localStorage.setItem(AuthService.LOGIN_TIME_KEY, new Date().toISOString());
            logger.log('💾 Dados do usuário armazenados localmente');
        } catch (error) {
            logger.error('❌ Erro ao armazenar dados do usuário:', error);
        }
    }

    /**
     * Recupera dados do usuário do localStorage
     */
    getStoredUser(): AuthenticatedUser | null {
        try {
            const userData = localStorage.getItem(AuthService.USER_STORAGE_KEY);
            if (userData) {
                return JSON.parse(userData) as AuthenticatedUser;
            }
        } catch (error) {
            logger.error('❌ Erro ao recuperar dados do usuário:', error);
            this.clearStoredUser(); // Limpa dados corrompidos
        }
        return null;
    }

    /**
     * Verifica se há um usuário logado
     */
    isLoggedIn(): boolean {
        return this.getStoredUser() !== null;
    }

    /**
     * Recupera informações sobre o login
     */
    getLoginInfo(): { user: AuthenticatedUser | null; loginTime: Date | null } {
        const user = this.getStoredUser();
        const loginTimeStr = localStorage.getItem(AuthService.LOGIN_TIME_KEY);
        const loginTime = loginTimeStr ? new Date(loginTimeStr) : null;

        return { user, loginTime };
    }

    /**
     * Realiza logout do usuário
     */
    logout(): void {
        try {
            localStorage.removeItem(AuthService.USER_STORAGE_KEY);
            localStorage.removeItem(AuthService.LOGIN_TIME_KEY);
            logger.log('🚪 Logout realizado com sucesso');
        } catch (error) {
            logger.error('❌ Erro durante logout:', error);
        }
    }

    /**
     * Limpa dados armazenados (usado em caso de erro)
     */
    private clearStoredUser(): void {
        try {
            localStorage.removeItem(AuthService.USER_STORAGE_KEY);
            localStorage.removeItem(AuthService.LOGIN_TIME_KEY);
        } catch (error) {
            logger.error('❌ Erro ao limpar dados do usuário:', error);
        }
    }

    /**
     * Verifica se o login ainda é válido (opcional - implementar expiração se necessário)
     */
    isLoginValid(): boolean {
        const { user, loginTime } = this.getLoginInfo();

        if (!user || !loginTime) {
            return false;
        }

        // Opcional: implementar expiração de login
        // Por exemplo, login válido por 24 horas:
        // const expirationTime = new Date(loginTime.getTime() + 24 * 60 * 60 * 1000);
        // return new Date() < expirationTime;

        return true; // Por enquanto, login não expira
    }
}

// Instância singleton do serviço de autenticação
export const authService = new AuthService();

// Exportações para facilitar o uso
export default authService;
